/**
 * React hooks for authentication
 */

import { useEffect } from 'react';
import { useAuthStore } from './auth-store';
import { AuthPolicy, Role, SmartFieldClaimType } from './types';
import { 
  hasPermission, 
  hasPermissionForSite,
  hasRole, 
  isAdmin, 
  isSiteManager,
  isSiteManagerForSite,
  isUserManager,
  isUserManagerForSite,
  isRawDataReader,
  isRawDataReaderForSite,
  isFluxDataReaderForSite,
  getAffiliatedSiteIds,
  getPermittedSiteIds,
  hasClaim,
  hasClaimForSite,
} from './permissions';

/**
 * Main authentication hook
 * Provides access to auth state and actions
 */
export function useAuth() {
  const store = useAuthStore();

  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    login: store.login,
    logout: store.logout,
    refreshTokens: store.refreshTokens,
    clearError: store.clearError,
  };
}

/**
 * Hook to check if user has a specific permission
 */
export function useHasPermission(policy: AuthPolicy): boolean {
  const { user } = useAuthStore();
  return hasPermission(user, policy);
}

/**
 * Hook to check if user has permission for a specific site
 */
export function useHasPermissionForSite(policy: AuthPolicy, siteId: number): boolean {
  const { user } = useAuthStore();
  return hasPermissionForSite(user, policy, siteId);
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(role: Role): boolean {
  const { user } = useAuthStore();
  return hasRole(user, role);
}

/**
 * Hook to check if user has a SmartField claim (any site)
 */
export function useHasClaim(claimType: SmartFieldClaimType): boolean {
  const { user } = useAuthStore();
  return hasClaim(user, claimType);
}

/**
 * Hook to check if user has a SmartField claim for a specific site
 */
export function useHasClaimForSite(claimType: SmartFieldClaimType, siteId: number): boolean {
  const { user } = useAuthStore();
  return hasClaimForSite(user, claimType, siteId);
}

/**
 * Hook to check if user is an admin
 */
export function useIsAdmin(): boolean {
  const { user } = useAuthStore();
  return isAdmin(user);
}

/**
 * Hook to check if user is a site manager (any site)
 */
export function useIsSiteManager(): boolean {
  const { user } = useAuthStore();
  return isSiteManager(user);
}

/**
 * Hook to check if user is a site manager for a specific site
 */
export function useIsSiteManagerForSite(siteId: number): boolean {
  const { user } = useAuthStore();
  return isSiteManagerForSite(user, siteId);
}

/**
 * Hook to check if user is a user manager (any site)
 */
export function useIsUserManager(): boolean {
  const { user } = useAuthStore();
  return isUserManager(user);
}

/**
 * Hook to check if user is a user manager for a specific site
 */
export function useIsUserManagerForSite(siteId: number): boolean {
  const { user } = useAuthStore();
  return isUserManagerForSite(user, siteId);
}

/**
 * Hook to check if user can read raw data (any site)
 */
export function useIsRawDataReader(): boolean {
  const { user } = useAuthStore();
  return isRawDataReader(user);
}

/**
 * Hook to check if user can read raw data for a specific site
 */
export function useIsRawDataReaderForSite(siteId: number): boolean {
  const { user } = useAuthStore();
  return isRawDataReaderForSite(user, siteId);
}

/**
 * Hook to check if user can read flux data for a specific site
 */
export function useIsFluxDataReaderForSite(siteId: number): boolean {
  const { user } = useAuthStore();
  return isFluxDataReaderForSite(user, siteId);
}

/**
 * Hook to get all sites the user has any access to
 */
export function useAffiliatedSiteIds(): number[] {
  const { user } = useAuthStore();
  return getAffiliatedSiteIds(user);
}

/**
 * Hook to get sites user has permission for with a specific claim type
 */
export function usePermittedSiteIds(claimType: SmartFieldClaimType): number[] {
  const { user } = useAuthStore();
  return getPermittedSiteIds(user, claimType);
}

/**
 * Hook to require authentication
 * Redirects to login if not authenticated
 */
export function useRequireAuth(redirectTo = '/login') {
  const { isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      window.location.href = redirectTo;
    }
  }, [isAuthenticated, isLoading, redirectTo]);

  return { isAuthenticated, isLoading };
}

/**
 * Hook to require specific permission
 * Redirects to unauthorized page if user doesn't have permission
 */
export function useRequirePermission(policy: AuthPolicy, redirectTo = '/unauthorized') {
  const { user, isLoading } = useAuthStore();
  const hasAccess = hasPermission(user, policy);

  useEffect(() => {
    if (!isLoading && !hasAccess) {
      window.location.href = redirectTo;
    }
  }, [hasAccess, isLoading, redirectTo]);

  return { hasAccess, isLoading };
}
